import * as React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Chip,
  Rating,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'
import { getPublicEventTrees, getPublicEventFeedback } from '../../../services'

const formatDateTime = value => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('es-EC')
}

const getEventStatus = event => {
  const now = new Date()
  const start = new Date(event.startDate)
  const end = new Date(event.endDate)

  if (!event.isActive) return { label: 'Desactivado', color: 'error' }
  if (now < start) return { label: 'Pendiente', color: 'warning' }
  if (now >= start && now <= end) return { label: 'En curso', color: 'success' }
  return { label: 'Finalizado', color: 'default' }
}

const getUserName = event => {
  const u = event.user
  return u ? `${u.nombre} ${u.apellido}` : event.userId || '—'
}

const InfoItem = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant="body2">{children}</Typography>
  </Box>
)

const SectionLoader = ({ message }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
    <CircularProgress size={16} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
)

const SectionError = ({ message, onRetry }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Alert severity="error" sx={{ py: 0 }}>
      {message}
    </Alert>
    <Box>
      <Button size="small" onClick={onRetry}>
        Reintentar
      </Button>
    </Box>
  </Box>
)

const initialAsyncState = { status: 'idle', error: null }

const PublicEventViewModal = (props, ref) => {
  const [open, setOpen] = React.useState(false)
  const [event, setEvent] = React.useState(null)
  const [treesState, setTreesState] = React.useState({ ...initialAsyncState, trees: [] })
  const [feedbackState, setFeedbackState] = React.useState({
    ...initialAsyncState,
    feedback: [],
    averageRating: null,
  })

  const loadTrees = React.useCallback(async eventId => {
    setTreesState(prev => ({ ...prev, status: 'loading', error: null }))
    try {
      const trees = await getPublicEventTrees(eventId)
      setTreesState({ status: 'success', error: null, trees: Array.isArray(trees) ? trees : [] })
    } catch (error) {
      setTreesState(prev => ({
        ...prev,
        status: 'error',
        error: error?.message || 'No fue posible cargar los árboles del evento.',
      }))
    }
  }, [])

  const loadFeedback = React.useCallback(async eventId => {
    setFeedbackState(prev => ({ ...prev, status: 'loading', error: null }))
    try {
      const response = await getPublicEventFeedback(eventId)
      setFeedbackState({
        status: 'success',
        error: null,
        feedback: Array.isArray(response?.feedback) ? response.feedback : [],
        averageRating: response?.averageRating ?? null,
      })
    } catch (error) {
      setFeedbackState(prev => ({
        ...prev,
        status: 'error',
        error: error?.message || 'No fue posible cargar los comentarios del evento.',
      }))
    }
  }, [])

  React.useImperativeHandle(ref, () => ({
    openModal: row => {
      setEvent(row)
      setOpen(true)
      loadTrees(row.id)
      loadFeedback(row.id)
    },
  }))

  const handleClose = () => {
    setOpen(false)
    setEvent(null)
    setTreesState({ ...initialAsyncState, trees: [] })
    setFeedbackState({ ...initialAsyncState, feedback: [], averageRating: null })
  }

  if (!event) return null

  const status = getEventStatus(event)
  const averageRating = Number(feedbackState.averageRating) || 0
  const comments = feedbackState.feedback.filter(
    item => typeof item?.comentario === 'string' && item.comentario.trim() !== '',
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 600 }}>
        Detalle del evento de Prueba
      </DialogTitle>

      <DialogContent dividers>
        {/* Cabecera */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <InfoItem label="Código">{event.code || '—'}</InfoItem>
          </Grid>
          <Grid item xs={12} sm={8}>
            <InfoItem label="Descripción">{event.descripcion || '—'}</InfoItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoItem label="Usuario asignado">{getUserName(event)}</InfoItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoItem label="Tenant">{event.tenant?.nombre || event.tenantId || '—'}</InfoItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoItem label="Inicio">{formatDateTime(event.startDate)}</InfoItem>
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoItem label="Fin">{formatDateTime(event.endDate)}</InfoItem>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Estado
            </Typography>
            <Box>
              <Chip label={status.label} color={status.color} size="small" />
            </Box>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Valoración
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating value={averageRating} precision={0.5} readOnly size="small" />
              <Typography variant="caption" color="text.secondary">
                {averageRating.toFixed(1)} / 5
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Chip label={`Accesos: ${event.accessCount ?? 0}`} size="small" color="primary" variant="outlined" />
          <Chip label={`Árboles: ${treesState.trees.length}`} size="small" color="success" variant="outlined" />
          <Chip label={`Comentarios: ${comments.length}`} size="small" variant="outlined" />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Árboles registrados */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Árboles registrados
        </Typography>
        {treesState.status === 'loading' || treesState.status === 'idle' ? (
          <SectionLoader message="Cargando árboles..." />
        ) : treesState.status === 'error' ? (
          <SectionError message={treesState.error} onRetry={() => loadTrees(event.id)} />
        ) : treesState.trees.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin árboles registrados.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Especie</TableCell>
                <TableCell>Registrado</TableCell>
                <TableCell>Colector</TableCell>
                <TableCell>Ubicación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {treesState.trees.map(tree => (
                <TableRow key={tree.id_a}>
                  <TableCell>{tree.codigo || '—'}</TableCell>
                  <TableCell>{tree.Specie?.nombre_cientifico || '—'}</TableCell>
                  <TableCell>{formatDateTime(tree.fechaRegistro)}</TableCell>
                  <TableCell>{tree.colectorRegistro || '—'}</TableCell>
                  <TableCell>{tree.referencia_ubicacion || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Comentarios */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Comentarios
        </Typography>
        {feedbackState.status === 'loading' || feedbackState.status === 'idle' ? (
          <SectionLoader message="Cargando comentarios..." />
        ) : feedbackState.status === 'error' ? (
          <SectionError message={feedbackState.error} onRetry={() => loadFeedback(event.id)} />
        ) : comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin comentarios aún.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {comments.map(comment => (
              <Box
                key={comment.id}
                sx={{
                  px: 1.2,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: '#FFFFFF',
                  border: theme => `1px solid ${theme.palette.divider}`,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Rating value={Number(comment.rating) || 0} readOnly size="small" />
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(comment.createdAt)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                  {comment.comentario}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  )
}

export default React.forwardRef(PublicEventViewModal)
